import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize, combineLatest, Subject, take, catchError, switchMap, tap, throwError, map } from 'rxjs';
import { AusPipelineService } from '../../../aus/aus-pipeline.service';
import { CmgUiUtilities } from '../../../cmg-1003/components/common/cmg-ui-utils';
import { Cmg1003Service } from '../../../cmg-1003/services/cmg-1003.service';
import { LoanApplicationService } from '../../../disclosure/services/loan-application.service';
import { LoanNotesQuery } from '../../../loan-state/loan-notes/loan-notes.query';
import { LoanNotesDataStore } from '../../../loan-state/loan-notes/loan-notes.store';
import { ConfirmationModalCloseType } from '../../../models';
import { UnsavedChangesDialogResponse } from '../../../shared/models';
import { LeadNotesQuery } from '../../../shared/state/lead-notes.query';
import { LeadNotesDataStore } from '../../../shared/state/lead-notes.store';
import { NoteListQuery } from '../../../shared/state/note-list.query';
import { NoteListStore } from '../../../shared/state/note-list.store';
import { FeatureFlagRepository, FeatureName } from '../../../core/state';
import { UiArea, UiQuery, UnsavedChangesService } from '../../../core/state/ui';
import { SaveDisclosuresRequest, ExtendedFieldValue, Prompt, Supplemental } from '../../../disclosure/models/loan-application.model';
import { LoanApplicationQuery } from '../../../disclosure/state/loan-application/loan-application.query';
import { Cmg1003LoanQuery, Cmg1003PropertyQuery } from '../../../loan-state';
import { SupplementalSectionPromptsQuery } from '../../../disclosure/state/supplemental-section-prompts/supplemental-section-prompts.query';
import { PartnersService, SpinnerService } from '../../../shared';
import { NotesPanelV2Service } from '../../../shared/components/notes-panel-v2/services/notes-panel-v2.service';
import { ApplicationNotesQuery } from '../../../shared/state/application-notes.query';
import { ApplicationNotesDataStore } from '../../../shared/state/application-notes.store';
import { DisclosureApplicationQuery } from '../../../disclosure/state/disclosure-application/disclosure-application.query';
import { PartnerNotesQuery } from '../../../shared/state/partner-notes.query';
import { PartnerNotesDataStore } from '../../../shared/state/partner-notes.store';

/**
 * QuickActionsUnsavedChangesService - This service is
 * designed to encapsulate all save/cancel/dismiss operations
 * for unsaved changes in quick actions panel.
 */
@Injectable({ providedIn: 'root' })
export class QuickActionsUnsavedChangesService {
  private readonly _applicationNotesQuery = inject(ApplicationNotesQuery);
  private readonly _applicationNotesStore = inject(ApplicationNotesDataStore);
  private readonly _ausPipelineService = inject(AusPipelineService);
  private readonly _cmg1003Service = inject(Cmg1003Service);
  private readonly _dialogService = inject(DialogService);
  private readonly _disclosureAppQuery = inject(DisclosureApplicationQuery);
  private readonly _featureFlagRepository = inject(FeatureFlagRepository);
  private readonly _leadNotesQuery = inject(LeadNotesQuery);
  private readonly _leadNotesStore = inject(LeadNotesDataStore);
  private readonly _loanApplicationQuery = inject(LoanApplicationQuery);
  private readonly _loanApplicationService = inject(LoanApplicationService);
  private readonly _loanNotesQuery = inject(LoanNotesQuery);
  private readonly _loanNotesStore = inject(LoanNotesDataStore);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _noteListQuery = inject(NoteListQuery);
  private readonly _noteListStore = inject(NoteListStore);
  private readonly _notesPanelV2Service = inject(NotesPanelV2Service);
  private readonly _partnerNotesQuery = inject(PartnerNotesQuery);
  private readonly _partnerNotesStore = inject(PartnerNotesDataStore);
  private readonly _partnersService = inject(PartnersService);
  private readonly _propertyQuery = inject(Cmg1003PropertyQuery);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _supplementalSectionPromptsQuery = inject(SupplementalSectionPromptsQuery);
  private readonly _uiQuery = inject(UiQuery);
  private readonly _unsavedChangesService = inject(UnsavedChangesService);

  private _unsavedChangesDialogResponse = UnsavedChangesDialogResponse;

  isDisclosuresDirty$ = combineLatest([
    this._featureFlagRepository.isFeatureEnabled$(FeatureName.Disclosures),
    this._loanApplicationService.isDirty$,
  ]).pipe(map(([isFeatureEnabled, isDirty]) => isFeatureEnabled && isDirty));

  resetQuickActionsPanel() {
    // waiter$ provides the caller a chance to use this
    // function in the subscription chain and call any further
    // action after store has been reset. This pattern is an alternate
    // to callback function and keeps the callback chain with the caller.
    const waiter$ = new Subject<unknown>();
    this.isPanelDirty$().subscribe(
      ({ isAusDirty, isLoanNotesDirty, isLeadNotesDirty, isApplicationNotesDirty, isDisclosuresDirty, isPartnerNotesDirty, isNoteListDirty }) => {
        if (isAusDirty) {
          this._ausPipelineService.reset();
        } else if (isLoanNotesDirty) {
          this._loanNotesStore.reset();
          this._loanNotesQuery.dirtyChecker2.dirtyChecker.reset();
        } else if (isNoteListDirty) {
          this._resetNoteListState();
        } else if (isLeadNotesDirty) {
          this._resetLeadNotesState();
        } else if (isApplicationNotesDirty) {
          this._resetApplicationNotesState();
        } else if (isDisclosuresDirty) {
          this._loanApplicationService.reset();
        } else if (isPartnerNotesDirty) {
          this._resetPartnerNotesState();
        }

        setTimeout(() => {
          this._resetWaiter(waiter$);
        });
      },
    );
    return waiter$.asObservable();
  }

  saveQuickActionsPanel(messageService?: MessageService) {
    // waiter$ provides the caller a chance to use this
    // function in the subscription chain and call any further
    // action after data has been saved. This pattern is an alternate
    // to callback function and keeps the callback chain with the caller.
    const waiter$ = new Subject<unknown>();
    this.isPanelDirty$().subscribe(
      ({
        isAusDirty,
        isLoanNotesDirty,
        isLeadNotesDirty,
        isApplicationNotesDirty,
        isDisclosuresDirty,
        isDisclosureApplicationsDirty,
        isPartnerNotesDirty,
        isNoteListDirty,
      }) => {
        // only one if these if conditions are to be true.
        if (isAusDirty) {
          this._ausPipelineService
            .saveAus()
            .pipe(
              take(1),
              finalize(() => {
                this._resetWaiter(waiter$);
              }),
            )
            .subscribe();
        } else if (isLoanNotesDirty) {
          this._saveLoanNotes(messageService, waiter$);
        } else if (isNoteListDirty) {
          this._saveNotesV2(messageService, waiter$);
        } else if (isApplicationNotesDirty) {
          this.saveApplicationNotes(messageService, waiter$);
        } else if (isLeadNotesDirty) {
          this.saveLeadNotes(messageService, waiter$);
        } else if (isDisclosureApplicationsDirty) {
          this._cmg1003Service.save1003(null, 'Saving Loan...');
          const saving1003Sub = this._cmg1003Service.isSaving1003$.asObservable().subscribe((isSaving) => {
            if (!isSaving) {
              if (isDisclosuresDirty) {
                this._saveDisclosure(messageService, waiter$);
              } else {
                this._resetWaiter(waiter$);
              }
              saving1003Sub.unsubscribe();
            }
          });
        } else if (isDisclosuresDirty) {
          this._saveDisclosure(messageService, waiter$);
        } else if (isPartnerNotesDirty) {
          this._savePartnerNotes(messageService, waiter$);
        } else {
          this._resetWaiter(waiter$);
        }
      },
    );
    return waiter$.asObservable();
  }

  private _saveDisclosure(messageService: MessageService, waiter$: Subject<unknown>) {
    const loanApplication = { ...this._loanApplicationQuery.getValue() };
    const supplementalPrompts: Prompt[] = this._supplementalSectionPromptsQuery.getAll();
    const vaSplitFeeID = this._loanQuery.getValue()?.vaSplitFeeID;
    const isNewConstructionID = this._propertyQuery.getValue()?.isNewConstructionID;
    let supplemental: Supplemental = null;

    if (loanApplication?.supplemental?.sections) {
      supplemental = { ...loanApplication.supplemental };
      supplemental.sections = this._loanApplicationService.getUpdatedSupplementalSection(loanApplication, supplementalPrompts);
    }

    const payload: SaveDisclosuresRequest = {
      fileDataId: loanApplication?.fileDataId,
      newConstruction: {
        id: isNewConstructionID,
        value: loanApplication?.newConstruction,
      },
      vaSplitFee: {
        id: vaSplitFeeID,
        value: loanApplication?.vaSplitFee === ExtendedFieldValue.Yes,
      },
      supplemental,
    };

    this._spinnerService.show();
    this._loanApplicationService
      .saveDisclosure(payload)
      .pipe(
        take(1),
        tap(() => {
          this._showSuccessToast(messageService);
        }),
        switchMap(() => this._loanApplicationService.refreshDisclosureState()),
        finalize(() => {
          this._resetWaiter(waiter$);
        }),
      )
      .subscribe(() => {
        this._loanApplicationService.reset();
        this._spinnerService.hide();
      });
  }

  /**
   * showUnsavedChangesDialog - common function to show
   * unsaved changes dialog for all unsaved changes in quick actions panel.
   * Note: It also handles the situation when there are no unsaved changes.
   * @param closable
   * @returns
   */
  showUnsavedChangesDialog(closable = false) {
    const result$ = new Subject<UnsavedChangesDialogResponse>();

    this._uiQuery
      .select()
      .pipe(take(1))
      .subscribe((ui) => {
        if (ui.isUnsavedChangesModalNeeded && ui.unsavedChangesAreaId === UiArea.pipelineUploadDocs) {
          this._unsavedChangesService.showUnsavedChangesModalIfNeeded(
            UiArea.pipelineUploadDocs,
            () => {
              result$.next(this._unsavedChangesDialogResponse.dismissViaXButton);
            },
            () => {
              result$.next(this._unsavedChangesDialogResponse.cancelAndContinue);
            },
            () => {
              result$.next(this._unsavedChangesDialogResponse.dismissViaXButton);
            },
          );
        } else {
          this.isPanelDirty$().subscribe(
            ({ isAusDirty, isLoanNotesDirty, isLeadNotesDirty, isDisclosuresDirty, isDisclosureApplicationsDirty, isNoteListDirty }) => {
              if (isAusDirty || isLoanNotesDirty || isLeadNotesDirty || isDisclosuresDirty || isDisclosureApplicationsDirty || isNoteListDirty) {
                const dialogRef = CmgUiUtilities.showUnsavedChangesModal(
                  this._dialogService,
                  `
                <p class="mt-0">Are you sure you want to leave this panel without saving?</p>
                <p class="mt-0">All changes will be lost if they are not saved.</p>
                `,
                  'Save and Continue',
                  'Continue without Saving',
                  closable,
                );

                dialogRef.onClose.subscribe((modalResult: ConfirmationModalCloseType) => {
                  if (modalResult === ConfirmationModalCloseType.ClosedViaAcceptButton) {
                    result$.next(UnsavedChangesDialogResponse.saveAndContinue);
                  } else if (modalResult === ConfirmationModalCloseType.ClosedViaRejectButton) {
                    result$.next(UnsavedChangesDialogResponse.cancelAndContinue);
                  } else {
                    /** Undefined - user closed it via x button */
                    result$.next(UnsavedChangesDialogResponse.dismissViaXButton);
                  }
                });
              } else {
                setTimeout(() => {
                  result$.next(UnsavedChangesDialogResponse.noNeedForDialog);
                });
              }
            },
          );
        }
      });

    return result$.asObservable();
  }

  /**
   * Saves leads notes and performs necessary downstream actions.
   *
   * Intended usages:
   * (1) Slide-in Panel - Notes: "Save" button.
   * (2) Unsaved Changes Modal: "Save and Continue" button.
   */
  saveLeadNotes(messageService?: MessageService, waiter$?: Subject<unknown>) {
    this._leadNotesQuery
      .select()
      .pipe(
        take(1),
        switchMap((leadNote) =>
          this._cmg1003Service.saveLeadNotes(leadNote).pipe(
            take(1),
            tap(() => {
              this._resetLeadNotesState();
              this._showSuccessToast(messageService);
            }),
            catchError((error) => {
              if (messageService) {
                messageService.add({ severity: 'error', summary: '', detail: 'Failed to save notes.' });
              }
              return throwError(error);
            }),
            finalize(() => {
              if (waiter$) {
                this._resetWaiter(waiter$);
              }
            }),
          ),
        ),
      )
      .subscribe();
  }

  saveApplicationNotes(messageService?: MessageService, waiter$?: Subject<unknown>) {
    this._applicationNotesQuery
      .select()
      .pipe(
        take(1),
        switchMap((LoanApplicationNote) => {
          const apiToSaveLoanApplicationNote = LoanApplicationNote?.guid
            ? this._cmg1003Service.saveLoanApplicationNotesPut(LoanApplicationNote)
            : this._cmg1003Service.saveLoanApplicationNotesPost(LoanApplicationNote);
          return apiToSaveLoanApplicationNote.pipe(
            take(1),
            tap(() => {
              this._resetApplicationNotesState();
              this._showSuccessToast(messageService);
            }),
            catchError((error) => {
              if (messageService) {
                messageService.add({ severity: 'error', summary: '', detail: 'Failed to save notes.' });
              }
              return throwError(error);
            }),
            finalize(() => {
              if (waiter$) {
                this._resetWaiter(waiter$);
              }
            }),
          );
        }),
      )
      .subscribe();
  }

  isPanelDirty$() {
    return combineLatest({
      isAusDirty: this._ausPipelineService.isDirty$,
      isLoanNotesDirty: this._loanNotesQuery.dirtyChecker2.isDirty$,
      isLeadNotesDirty: this._leadNotesQuery.dirtyChecker.isDirty$,
      isApplicationNotesDirty: this._applicationNotesQuery.dirtyChecker2.dirtyChecker.isDirty$,
      isDisclosuresDirty: this.isDisclosuresDirty$,
      isDisclosureApplicationsDirty: this._disclosureAppQuery.dirtyChecker.isDirty$,
      isPartnerNotesDirty: this._partnerNotesQuery.dirtyChecker2.isDirty$,
      isNoteListDirty: this._noteListQuery.dirtyChecker2.isDirty$,
    }).pipe(take(1));
  }

  private _resetLeadNotesState() {
    this._leadNotesStore.reset();
    this._leadNotesQuery.dirtyChecker.reset();
  }

  private _resetApplicationNotesState() {
    this._applicationNotesStore.reset();
    this._applicationNotesQuery.dirtyChecker2.dirtyChecker.reset();
  }

  private _resetPartnerNotesState() {
    this._partnerNotesStore.reset();
    this._partnerNotesQuery.dirtyChecker2.dirtyChecker.reset();
  }

  private _resetNoteListState() {
    this._noteListStore.resetEditor();
    this._noteListQuery.dirtyChecker2.dirtyChecker.reset();
  }

  private _resetWaiter(waiter$: Subject<unknown>) {
    waiter$.next(null);
    waiter$.complete();
  }

  /**
   * Saves Loan notes and performs necessary downstream actions.
   *
   * Intended usages:
   * (1) Slide-in Panel - Notes: "Save" button.
   * (2) Unsaved Changes Modal: "Save and Continue" button.
   */
  private _saveLoanNotes(messageService?: MessageService, waiter$?: Subject<unknown>) {
    this._cmg1003Service
      .saveLoanNotes()
      .pipe(
        take(1),
        finalize(() => {
          this._resetWaiter(waiter$);
        }),
      )
      .subscribe(() => {
        this._loanNotesQuery.dirtyChecker2.dirtyChecker.setHead();
        this._showSuccessToast(messageService);
      });
  }

  private _savePartnerNotes(messageService?: MessageService, waiter$?: Subject<unknown>) {
    this._partnersService
      .upsertPartnerNote()
      .pipe(
        take(1),
        finalize(() => {
          this._resetWaiter(waiter$);
        }),
      )
      .subscribe(() => {
        this._partnerNotesQuery.dirtyChecker2.dirtyChecker.setHead();
        this._showSuccessToast(messageService);
      });
  }

  /**
   * Saves Notes V2 via the service's store-based saveCurrentNote().
   * Reads content and tags from NoteListStore, so it works even after the component is destroyed.
   */
  private _saveNotesV2(messageService?: MessageService, waiter$?: Subject<unknown>) {
    this._notesPanelV2Service
      .saveCurrentNote()
      .pipe(
        take(1),
        tap(() => {
          this._noteListQuery.dirtyChecker2.dirtyChecker.reset();
          this._showSuccessToast(messageService, 'Note saved successfully.');
        }),
        catchError((error) => {
          if (messageService) {
            messageService.add({ severity: 'error', summary: '', detail: 'Failed to save note. Please try again.' });
          }
          return throwError(error);
        }),
        finalize(() => {
          this._resetWaiter(waiter$);
        }),
      )
      .subscribe();
  }

  private _showSuccessToast(messageService?: MessageService, detail = 'Data saved successfully!') {
    if (messageService) {
      messageService.add({ severity: 'success', summary: '', detail: detail });
    }
  }
}
